import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  BreadcrumbItem,
  InfoItem,
  ResponsiveService,
} from '@frk/eds-components';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TableResponsiveService } from '@shared/table/table-responsive.service';
import { from, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { LiteratureYourCartOrderSuccessService } from './services/literature-your-cart-order-success.service';
import cloneDeep from 'lodash/cloneDeep';
import { TranslateService } from '@shared/translate/translate.service';
import { IUserProfile } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import {
  CartActionPerformed,
  LiteratureDocument,
  LiteratureDocumentListingState,
  MailFormValues,
  MailResponseObject,
  MailResponseError,
  LineItem,
} from '@types';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { StorageService } from '@services';
import { FIRM_TOKEN } from '@utils/app.constants';

@Component({
  selector: 'ft-literature-your-cart-mail-order-success',
  templateUrl: './literature-your-cart-mail-order-success.component.html',
  styleUrls: ['./literature-your-cart-mail-order-success.component.scss'],
  providers: [TableResponsiveService],
})
export class LiteratureYourCartMailOrderSuccessComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  isHandheld$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  cartItems$: Observable<LiteratureDocumentListingState>;
  orderFormDetailsValue: MailFormValues;
  postMailResponse: MailResponseObject;
  postMailError: MailResponseError;
  cards: InfoItem[][] = [];
  OrderNotSuccessReason: string[] = [];
  orderNotsuccessMsg: string;
  orderDate;
  showHistory = false;
  cartItems;
  successPageBreadcrumbs: BreadcrumbItem[];
  pageStatus: string;
  profileData: IUserProfile;
  orderHistoryButtonLabel: string;
  @ViewChild('tableResponsive', { static: false })
  public set table(table: ElementRef) {
    if (table) {
      this.tableResponsiveService?.init(table);
    }
  }

  private unsubscribe$: Subject<void> = new Subject<void>();

  public isPrimericaUser = false;

  constructor(
    private responsiveService: ResponsiveService,
    private cartService: CartHandlerService,
    private changeDetectorRef: ChangeDetectorRef,
    protected tableResponsiveService: TableResponsiveService,
    private translateService: TranslateService,
    private literatureYourCartOrderSuccessService: LiteratureYourCartOrderSuccessService,
    private profileService: ProfileService,
    private literatureConfig: LiteratureGlobalConfigService,
    private storageService: StorageService
  ) {
    super();
    this.tableResponsiveService.cards$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((cards) => {
        this.cards = cards;
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnInit(): void {
    const firmToken$ = from(this.storageService.retrieve(FIRM_TOKEN));
    this.setBreadcrumbItems();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isMobile$ = this.responsiveService.isMobile$();
    this.changeDetectorRef.detectChanges();
    this.orderHistoryButtonLabel = this.translateService.instant(
      `literature.order-history`
    );
    this.orderFormDetailsValue = this.literatureYourCartOrderSuccessService.getMailFormValue();
    this.postMailResponse = this.literatureYourCartOrderSuccessService.postMailResponse();
    this.postMailError = this.literatureYourCartOrderSuccessService.postMailError();
    if (this.postMailError) {
      this.OrderNotSuccessReason = [
        `${
          this.postMailError.description
            ? this.postMailError.description
            : this.translateService.instant(`literature.technical-error`)
        }`,
      ];
      this.pageStatus = this.translateService.instant(
        `literature.order-unsuccessful`
      );
      this.createBreadcrumbItem(this.pageStatus);
    } else if (!this.postMailResponse?.orderConfirmationNumber) {
      this.pageStatus = this.translateService.instant(
        `literature.order-unsuccessful`
      );
      this.createBreadcrumbItem(this.pageStatus);
      this.OrderNotSuccessReason = this.postMailResponse?.lineItem?.map(
        (item: LineItem) => {
          if (item.status?.length > 0) {
            return `${item.status[0]?.message} (${item.packageCode}-${item.literatureType})`;
          }
        }
      );
    } else {
      this.cartService
        .getCartItems()
        .pipe(
          takeUntil(this.unsubscribe$),
          map((data) =>
            this.cartService.checkCheckoutEligibilty(cloneDeep(data))
          )
        )
        .subscribe((items) => {
          this.cartItems = items;
          this.pageStatus = this.translateService.instant(
            `literature.order-successful`
          );
          this.createBreadcrumbItem(this.pageStatus);
          // remove docs from storage for which order has been successfully placed.
          const docIds = this.postMailResponse?.lineItem?.map(
            (item: LineItem) => {
              return `${item.packageCode}-${item.literatureType}`;
            }
          );
          this.cartService.removeFromCart(
            docIds,
            CartActionPerformed.DELETED_AFTER_ORDER_SUCCESS
          );
        });
    }
    this.orderDate = Date.now();
    this.showHistory = this.literatureConfig.IsUserLoggedInNotShareHolder();

    firmToken$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((firmToken: string) => {
        this.isPrimericaUser = firmToken === 'partner-primerica';
      });
  }

  createBreadcrumbItem(pageStatus: string): void {
    if (this.showBreadcrumb) {
      this.successPageBreadcrumbs = this.breadcrumbItems;
      this.successPageBreadcrumbs[this.successPageBreadcrumbs.length - 1] = {
        content: pageStatus,
      };
    }
  }

  getOverAllQty(cartItems: LiteratureDocument[]): number {
    return this.cartService.getOverAllQuantity(cartItems);
  }

  removeAndGetLinkValue(item): void {
    this.tableResponsiveService.removeValue(item);
  }

  /**
   * get literature details page link
   * @param url string
   * @returns string
   */
  getLiteratureDetailLink(docId: string): string {
    return `${this.literatureConfig?.navigationUrls?.litDetailUrl}/${docId}`;
  }

  navigateTo(buttonType: string) {
    if (buttonType === 'primary') {
      this.navigateToLiteraturePage();
    } else {
      this.navigateToOrderHistory();
    }
  }

  navigateToOrderHistory() {
    this.literatureConfig.navigateToUrl(
      this.literatureConfig.navigationUrls?.orderHistoryList
    );
  }

  navigateToLiteraturePage() {
    this.literatureConfig.navigateToUrl(
      this.literatureConfig.navigationUrls?.litListing
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
